import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, History } from 'lucide-react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  Button,
  Skeleton,
  EmptyState,
  Alert,
} from '@insolech/ui';
import { patientQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import type { PrescriptionDeepState } from './prescription/prescriptionState';
import type { SolutionSubtype } from './SolutionSubtypePicker';
import { evaluateReuse, reuseNote, type ReuseVerdict } from './prescriptionReuse';

/**
 * PriorOrderPickerModal — prior orders for the selected patient, with optional
 * prescription reuse, ACROSS product types.
 *
 * When an existing patient is selected in the new-order wizard, the wizard
 * opens this modal listing up to the patient's 10 most recent orders. The
 * podiatrist sees the order context (number, date, product label, short
 * prescription summary) and can:
 *   - click "View" on a row to open that order in a new tab (does NOT touch
 *     wizard state);
 *   - click "Use this prescription" on ANY row that holds one, seeding this
 *     prescription from it. A row from a different product is allowed and says
 *     what will not carry across;
 *   - "Keep what I have", which closes and changes nothing; or
 *   - "Start fresh", which resets the prescription from defaults, and asks
 *     first when that would discard content the operator already entered.
 *
 * Reuse used to require an EXACT subtype match. It no longer does: the
 * patient-step reorder search already copied across products with no filter at
 * all, so the restriction only applied to whichever door the operator happened
 * to use. See the header of ./prescriptionReuse for the full reasoning, the
 * shared intersect-by-target rule, and every remaining refusal path.
 *
 * Rendered WITHOUT `onUsePrescription` (e.g. outside the wizard) the modal is
 * view-only: no reuse buttons, and the footer is a plain Close.
 *
 * Data source: the `patient_orders_history` RPC, wrapped by
 * `patientQueries.patientHistoryQuery`. It DOES return the configuration JSONB
 * (since migration 20261108000000), which is what makes reuse possible. Rows
 * are ordered DESC by created_at on the SQL side; we slice to 10 here. Note it
 * exposes only the order's FIRST line (`order by ol.line_number asc limit 1`),
 * so multi-line cart orders surface only line 1 - see the backlog.
 */

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  patientId: string;
  patientName?: string | undefined;
  /** Subtype of the line being created. Decides which prescription regions a
   *  reuse carries across; it no longer decides WHETHER one is allowed. */
  targetSubtype?: SolutionSubtype | null | undefined;
  /** True when the operator has already entered prescription content, so a
   *  reset would discard real work and must be confirmed first. */
  prescriptionHasContent?: boolean | undefined;
  /** Apply a past line's prescription as the starting point for this one.
   *  `undefined` deep means "reset to defaults". `fromOrderNumber` is echoed
   *  back so the wizard can tell the operator what was applied - with every
   *  section collapsed, a successful apply and a no-op otherwise look
   *  identical. */
  onUsePrescription?:
    | ((
        deep: PrescriptionDeepState | undefined,
        observations?: string | undefined,
        fromOrderNumber?: string | undefined,
      ) => void)
    | undefined;
}

export function PriorOrderPickerModal({
  open,
  onOpenChange,
  patientId,
  patientName,
  targetSubtype,
  prescriptionHasContent = false,
  onUsePrescription,
}: Props) {
  const [confirmingReset, setConfirmingReset] = React.useState(false);
  // Only fetch once the modal is open AND we have a real patient id. The
  // query stays cached so a quick "open → close → reopen" doesn't re-hit
  // the network.
  // PER-LINE, not per-order. patient_orders_history collapses each order to
  // its first line, so a reorder cart (normal since PR #85) hid its other
  // lines entirely: 44 lines in production are invisible to it. Choosing a
  // prescription to reuse is a per-line decision, so the list is per line.
  const historyQuery = useQuery({
    ...patientQueries.patientLineHistoryQuery(supabase, patientId),
    enabled: open && !!patientId,
  });

  // Adapted to the shape the row renderer and evaluateReuse already read.
  const rows = (historyQuery.data ?? []).slice(0, 10).map((l) => ({
    line_id: l.line_id,
    line_number: l.line_number,
    order_id: l.order_id,
    order_number: l.order_number,
    created_at: l.created_at,
    status: l.status,
    primary_line_type: l.line_type,
    primary_solution_subtype: l.solution_subtype,
    primary_product_name: l.product_name,
    primary_configuration: l.configuration,
    material: patientQueries.lineMaterial(l),
  }));

  // Two lines of the same cart can share a subtype and material, so the order
  // number alone would render them identically. Only shown when it disambiguates.
  const multiLineOrders = new Set(
    rows
      .map((r) => r.order_id)
      .filter((id, i, all) => all.indexOf(id) !== i),
  );

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Prior orders for this patient</ModalTitle>
          <ModalDescription>
            {patientName
              ? `We found previous orders on ${patientName}'s file. Review them for context, then continue when ready.`
              : 'We found previous orders on this patient. Review them for context, then continue when ready.'}
          </ModalDescription>
        </ModalHeader>

        <ModalBody>
          {historyQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : historyQuery.isError ? (
            <Alert tone="error" title="Could not load prior orders">
              {String(
                (historyQuery.error as { message?: string } | undefined)
                  ?.message ?? 'Please try again.',
              )}
            </Alert>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<History className="h-6 w-6" />}
              title="No prior orders"
              description="This patient has no previous orders on file."
            />
          ) : (
            <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
              {rows.map((r) => (
                <li key={r.line_id}>
                  <PriorOrderRow
                    orderId={r.order_id}
                    orderNumber={r.order_number}
                    createdAt={r.created_at}
                    productLabel={
                      multiLineOrders.has(r.order_id)
                        ? `${formatRowProductLabel(r)} · line ${r.line_number}`
                        : formatRowProductLabel(r)
                    }
                    prescriptionSummary={formatRowPrescriptionSummary(r)}
                    {...reuseProps(
                      evaluateReuse(r, targetSubtype),
                      onUsePrescription,
                      (deep, obs) => {
                        // A verbatim carry of the regions this target renders.
                        // No value translation: whatever the target does not
                        // offer is caught by the red banner and the submit
                        // block, the same protection the reorder route uses.
                        // Podiatrist observations are prescription-level and
                        // type-agnostic; the reorder path carries them, so this
                        // one must too.
                        onUsePrescription?.(deep, obs, r.order_number);
                        onOpenChange(false);
                      },
                    )}
                  />
                </li>
              ))}
            </ul>
          )}
        </ModalBody>

        <ModalFooter>
          {/*
            This modal AUTO-OPENS on every existing-patient pick, and the patient
            section sits inside the prescription form, so the operator may well
            have filled the prescription in already. "Start fresh" resets it from
            defaults, which is correct when there is nothing to lose and
            destructive when there is - and with every section collapsed by
            default, the loss is invisible. So: offer keeping what is there as a
            first-class option, and make the reset ASK when it would actually
            discard work.
          */}
          {onUsePrescription && confirmingReset ? (
            <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-warning-800">
                This clears the prescription you have already started. Continue?
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmingReset(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={() => {
                    onUsePrescription(undefined, undefined, undefined);
                    setConfirmingReset(false);
                    onOpenChange(false);
                  }}
                >
                  Clear and start fresh
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="primary"
                onClick={() => onOpenChange(false)}
              >
                {onUsePrescription ? 'Keep what I have' : 'Close'}
              </Button>
              {onUsePrescription ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    if (prescriptionHasContent) {
                      setConfirmingReset(true);
                      return;
                    }
                    // Nothing to lose - reset silently from defaults.
                    onUsePrescription(undefined, undefined, undefined);
                    onOpenChange(false);
                  }}
                >
                  Start fresh
                </Button>
              ) : null}
            </div>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

interface PriorOrderRowProps {
  orderId: string;
  orderNumber: string;
  createdAt: string;
  productLabel: string;
  prescriptionSummary: string;
  /** Present only when the modal can apply a prescription (wizard context). */
  onUse?: (() => void) | undefined;
  /** Why reuse is unavailable for this row. */
  reuseBlockedReason?: string | undefined;
  /** What this reuse will and will not bring across, when that is not obvious
   *  (a different product, or a region this target does not render). */
  reuseNote?: string | undefined;
}

function PriorOrderRow({
  orderId,
  orderNumber,
  createdAt,
  productLabel,
  prescriptionSummary,
  onUse,
  reuseBlockedReason,
  reuseNote,
}: PriorOrderRowProps) {
  return (
    // Stacks below sm (480px in this repo, NOT 640) - the actions previously
    // sat in a shrink-0 row that squeezed the label to a few characters on a
    // phone.
    <div className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="min-w-0">
        {/*
          Row layout per Rafael:  ORDER_ID | DATE | PRODUCT | (short
          prescription summary). On narrow viewports we wrap; on wide the
          flex-row keeps everything on one line and truncates.
        */}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
          <span className="font-mono text-xs text-text-secondary">
            {orderNumber}
          </span>
          <span className="text-xs text-text-muted">
            {formatShortDate(createdAt)}
          </span>
          <span className="font-medium text-text-primary truncate">
            {productLabel}
          </span>
        </div>
        {prescriptionSummary ? (
          <p className="text-xs text-text-secondary mt-1 truncate">
            {prescriptionSummary}
          </p>
        ) : null}
        {/* In the LEFT column on purpose. The actions cell is shrink-0, so a
            sentence in there squeezes the product label down to a few
            characters on a phone, the same failure the row layout comment
            above records. NOT truncated, unlike the summary: a half-shown
            "Sandal style will not car…" is worse than no warning, because it
            reads as decoration. */}
        {reuseNote ? (
          <p className="text-xs text-warning-800 mt-1">{reuseNote}</p>
        ) : null}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <a
          href={`/orders/${orderId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 hover:underline"
        >
          View
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
        {onUse ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onUse}
            data-testid={`use-prescription-${orderId}`}
          >
            Use this prescription
          </Button>
        ) : reuseBlockedReason ? (
          // The explanation was previously a `title` on a non-focusable span,
          // which keyboard and touch users can never reach. The reason is now
          // the visible text itself, and the longer explanation rides along in
          // the accessible name rather than a hover-only tooltip.
          <span
            className="text-xs text-text-muted"
            aria-label={`Cannot reuse this prescription: ${reuseBlockedReason}.`}
          >
            {reuseBlockedReason}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Row formatters - keep them dumb / local to this modal.                    */
/* -------------------------------------------------------------------------- */

/** Short, locale-friendly date (e.g. "10 Jun 2026"). */
function formatShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

/** Map a verdict onto the row's optional props. Reuse controls are shown only
 *  in wizard context (onUsePrescription present); the modal is view-only from
 *  anywhere else. The rules themselves live in ./prescriptionReuse so they can
 *  be unit-tested without this file's eager supabase import. */
function reuseProps(
  verdict: ReuseVerdict,
  onUsePrescription: Props['onUsePrescription'],
  apply: (deep: PrescriptionDeepState, observations: string | undefined) => void,
): { onUse?: () => void; reuseBlockedReason?: string; reuseNote?: string } {
  if (!onUsePrescription) return {};
  if (!verdict.ok) return { reuseBlockedReason: verdict.reason };
  const note = reuseNote(verdict);
  return {
    onUse: () => apply(verdict.deep, verdict.observations),
    // Spread-safe: this object lands in JSX via {...reuseProps(...)} and the
    // repo runs exactOptionalPropertyTypes, so an explicit `undefined` is a
    // type error rather than an absent prop.
    ...(note ? { reuseNote: note } : {}),
  };
}

function humanise(v: string): string {
  return v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Row label: product TYPE + SUBTYPE + MATERIAL, which is what the podiatrist
 * needs to tell "PA12 insole" from "carbon insole" from "sandals" when picking
 * a prescription to reuse. The RPC exposes primary_solution_subtype and the
 * configuration JSONB (material lives there), so no extra query is needed.
 */
function formatRowProductLabel(r: {
  primary_line_type: string | null;
  primary_product_name: string | null;
  primary_solution_subtype?: string | null;
  primary_configuration?: Record<string, unknown> | null;
}): string {
  const parts: string[] = [];
  if (r.primary_solution_subtype) parts.push(humanise(r.primary_solution_subtype));
  else if (r.primary_product_name) parts.push(r.primary_product_name);
  else if (r.primary_line_type) parts.push(humanise(r.primary_line_type));

  const material = (r.primary_configuration ?? {})['material'];
  if (typeof material === 'string' && material.trim()) {
    const m = material.trim();
    // Do not repeat the material when the subtype already names it
    // (e.g. "Carbon Insole" + "Carbon").
    if (!parts[0] || !parts[0].toLowerCase().includes(m.toLowerCase())) parts.push(m);
  }
  return parts.length > 0 ? parts.join(' · ') : '-';
}

/**
 * One-line "what was this order about" cue, so the podiatrist does not have to
 * open a tab: a line-type / status blend (e.g. "Custom prescription,
 * delivered"). Deliberately coarse - the detail lives behind "View".
 */
function formatRowPrescriptionSummary(r: {
  status: string;
  primary_line_type: string | null;
}): string {
  const parts: string[] = [];
  if (r.primary_line_type === 'custom_insole' || r.primary_line_type === 'premium_3d_insole') {
    parts.push('Custom prescription');
  } else if (r.primary_line_type === 'ots_insole') {
    parts.push('Ready-made insole');
  } else if (r.primary_line_type === 'other_equipment') {
    parts.push('Marketplace item');
  } else if (r.primary_line_type === 'orthopedic_sandal') {
    parts.push('Orthopedic sandal');
  }
  if (r.status) {
    parts.push(humaniseStatus(r.status));
  }
  return parts.join(', ');
}

function humaniseStatus(s: string): string {
  return s.replace(/_/g, ' ');
}
